import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

/** Admin-only auto-assign button + preview dialog.
 *  Kicks a dry-run first, shows the plan (per-emp fair-share stats + unfilled),
 *  and requires a click to apply.  */
export default function AutoAssignButton({ facilityId, payPeriodId, onDone }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);

  const runDry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/schedule/auto-assign", {
        facility_id: facilityId, pay_period_id: payPeriodId, dry_run: true,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-assign failed");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/schedule/auto-assign", {
        facility_id: facilityId, pay_period_id: payPeriodId, dry_run: false,
      });
      toast.success(`Auto-assigned ${data.applied} shifts`);
      setPreview(null); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  return (
    <>
      <Button
        variant="outline"
        className="border-fuchsia-400 text-fuchsia-700 hover:bg-fuchsia-50 hidden md:inline-flex"
        onClick={runDry}
        disabled={busy}
        data-testid="btn-auto-assign"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles size={14} className="md:mr-1" />}
        <span className="hidden md:inline">{busy ? "Assigning…" : "Auto-Assign"}</span>
      </Button>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="auto-assign-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-600" />
              Auto-Assign Preview
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Kpi label="Open shifts"     v={preview.total_open} tone="amber"/>
                <Kpi label="Would fill"      v={preview.planned}   tone="emerald"/>
                <Kpi label="Unfilled"        v={preview.unfilled.length} tone="red"/>
                <Kpi label="Fair share"      v={preview.fair_share} tone="slate"/>
                <Kpi label="Missing avail."  v={preview.missing_availability.length} tone="orange"/>
              </div>

              <div className="rounded-xl border border-slate-200">
                <div className="px-3 py-2 border-b border-slate-200 text-sm font-semibold">
                  Per-Diem Distribution
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-[10px]">
                      <tr>
                        <th className="text-left p-2">Employee</th>
                        <th className="text-center p-2">Submitted?</th>
                        <th className="text-center p-2">Avail days</th>
                        <th className="text-center p-2">Assigned</th>
                        <th className="text-center p-2">% avail.</th>
                        <th className="text-left p-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.per_emp_stats.map(s => (
                        <tr key={s.employee_id} className="border-t border-slate-100" data-testid={`aa-row-${s.employee_id}`}>
                          <td className="p-2">{s.name}</td>
                          <td className="p-2 text-center">{s.submitted ? "Yes" : <span className="text-red-600 font-bold">MISSING</span>}</td>
                          <td className="p-2 text-center">{s.availability_days}</td>
                          <td className="p-2 text-center font-semibold">{s.assigned}</td>
                          <td className="p-2 text-center">{s.pct_of_availability}%</td>
                          <td className="p-2 text-[10px] text-slate-500">
                            {s.rare_skill_notes?.join(", ") || ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {preview.unfilled.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-red-800 mb-1">
                    <AlertTriangle className="w-4 h-4" /> Unfilled shifts ({preview.unfilled.length})
                  </div>
                  <div className="text-[11px] text-red-700 max-h-32 overflow-y-auto space-y-0.5">
                    {preview.unfilled.map((u, i) => (
                      <div key={i}>· {u.date} · <span className="font-mono">{u.shift_code}</span> ({u.category}) — {u.reason}</div>
                    ))}
                  </div>
                </div>
              )}

              {preview.missing_availability.length > 0 && (
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-xs text-orange-800">
                  <b>Per-diems missing availability:</b> {preview.missing_availability.map(m => m.name).join(", ")}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} disabled={applying} data-testid="aa-cancel">
              Cancel
            </Button>
            <Button
              className="bg-fuchsia-600 hover:bg-fuchsia-700"
              onClick={apply}
              disabled={applying || !preview || preview.planned === 0}
              data-testid="aa-apply"
            >
              {applying ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Applying…</> :
                <><CheckCircle2 className="w-4 h-4 mr-1" /> Apply {preview?.planned || 0} assignments</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Kpi({ label, v, tone }) {
  const map = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    red: "bg-red-50 border-red-200 text-red-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    orange: "bg-orange-50 border-orange-200 text-orange-900",
  };
  return (
    <div className={`rounded-lg border p-2 ${map[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-black">{v}</div>
    </div>
  );
}
